import type { Me } from '@pickpro/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { advanceSessionGeneration } from '../api/session-generation';
import { resetLiveSocket } from '../live/live-socket-state';
import { clearSessionCache, ME_QUERY_KEY, replaceSessionCache } from './session-cache';
import type { SessionMessage } from './session-channel';
import { hasSessionHint } from './session-hint';

const dropSession = (client: QueryClient): Promise<void> =>
  clearSessionCache(client, advanceSessionGeneration());

const adoptSession = async (client: QueryClient, user: Me): Promise<void> => {
  const cached = client.getQueryData<Me | null>(ME_QUERY_KEY);
  if (cached?.id === user.id) return;
  if (cached) resetLiveSocket();
  await replaceSessionCache(client, user, advanceSessionGeneration());
};

export const applySessionMessage = async (
  client: QueryClient,
  message: SessionMessage,
): Promise<void> => {
  if (message.type === 'signed-out') {
    if (!hasSessionHint()) await dropSession(client);
    return;
  }
  if (hasSessionHint()) await adoptSession(client, message.user);
};

export const reconcileSessionHint = async (client: QueryClient): Promise<void> => {
  const cached = client.getQueryData<Me | null>(ME_QUERY_KEY);
  if (!hasSessionHint()) {
    if (cached) await dropSession(client);
    return;
  }
  if (cached === null) await client.invalidateQueries({ queryKey: ME_QUERY_KEY });
};
